/*
 * SidebarItem
 * @module Sidebar
 * @flow
 */

import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';

import { Tab } from '~/components/Tabs/components/Tab';
import { type TabProps } from '~/components/Tabs/components/Tab/Tab';

export type SidebarItemProps = TabProps & {
  open?: boolean,
  palette?: string,
  onClick?: () => void,
};

const baseColor = palette('grayscale', 0, true);

/**
 * SidebarItem React-component
 * @memberof Sidebar
 * @extends Tab
 * @param {boolean} [props.open] - sidebar open state
 * @param {Function} [props.onClick] - click handler
 * @example <caption>Simple usage</caption>
 * <SidebarItem icon={<IconWallet />}>My Wealth</SidebarItem>
 * @returns {React.Node<*>} React-component
 */
export const SidebarItem = styled(Tab)`
  min-height: 20px;
  height: 40px;
  color: ${baseColor};
  justify-content: flex-start;
  opacity: 0.8;
  overflow: hidden;
  white-space: nowrap;

  ${ifProp(
    'open',
    css`
      padding: 4px 12px !important;

      .react-tab-icon {
        margin-right: 12px;
      }
    `,
    css`
      padding: 4px 13px;
      justify-content: center;

      span {
        display: none;
      }

      .react-tab-icon {
        margin-right: 0;
      }
    `,
  )};

  &:hover {
    color: ${baseColor};
    font-weight: normal;
    opacity: 1;
  }

  &.react-tab__active {
    padding-left: ${ifProp('open', '12px', '12px')};
    background-color: ${palette(2)};
    border-bottom: 0 none;
    border-left: 2px solid ${baseColor};
    color: ${baseColor};
    font-weight: normal;
    opacity: 1;
  }

  svg {
    width: auto;
    color: ${baseColor};
  }

  &.react-tab {
    font-size: 13px;
  }

  &.react-tab + .react-tab {
    margin-top: 8px;
    font-size: 13px;
    margin-left: 0;
  }
`;

SidebarItem.defaultProps = {
  open: true,
  palette: 'primary',
  testid: 'sidebaritem',
  onClick: () => {},
};
